import React from 'react'
import { i18n, baseLocales } from '@fs/zion-locale'
import { Link } from '@fs/zion-router'
import { Row, colors, Skeleton } from '@fs/zion-ui'
import { ArrowCaret } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { useCanAddPersonalData } from '@fs/zion-permissions'
import { css } from '@linaria/core'
import LanguageLink from '../components/LanguageLink'
import { useDynamicLinkContext } from '../DynamicLinksProvider'
import { buildUrl } from '../footerHelperFunctions'
import { isArabicCountry } from '../hooks/useShowArabicExperience'

const linkCss = css`
  color: ${colors.transparent.gray70} !important; // needed because of isGray and noLinkStyles conflicting

  &:hover,
  &:focus {
    text-decoration: underline;
  }
`
const moreButton = css`
  display: flex;
  align-items: center;
  color: ${colors.transparent.gray70};
  background: none;
  border: none;
  font-size: 14px;
  margin-inline-start: -5px;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
  span {
    margin-inline-end: 8px;
  }
`

function useOldMinNavLinks({ nonReligiousMode, showArabicExperience, external }) {
  const showMobileAppsLink = useFeatureFlag('home_footer_mobileAppsLink')

  // The reason we aren't using showArabicExperience here is because showArabicExperience considers if you are a member and we don't care about that here.
  const { isOn: arabicContactUsFlagOn } = useFeatureFlag('home_footer_arabicContactUsLink')
  const useArabicContactUs = arabicContactUsFlagOn && (isArabicCountry() || i18n.language === 'ar')
  const contactUsLink = useArabicContactUs
    ? `/fieldops/middle-east-contact-us`
    : `/fieldops/familysearch-support-contact-us`

  const [isAuthorized] = useCanAddPersonalData()
  const { t } = useTranslation()
  const isCoreLanguage = baseLocales.includes(i18n.language)
  const nonEnglishSupportedBlogLang = ['es', 'pt', 'de', 'fr', 'ru', 'it', 'nl', 'pl', 'sv', 'ko', 'ja', 'zh'].includes(
    i18n.language
  )
  // non supported languages as well as English will show the blog home page (English version)
  const blogLink = nonEnglishSupportedBlogLang ? `/${i18n.language}/blog/${i18n.language}` : `/${i18n.language}/blog/`

  return [
    {
      url: '/about',
      linkName: 'footer_about',
      text: t('nav.about-familysearch', 'About'),
    },
    {
      url: '/info/volunteer/',
      linkName: 'footer_volunteer',
      text: t('nav.help.volunteer', 'Volunteer'),
      show: !nonReligiousMode && isCoreLanguage && !showArabicExperience,
    },
    {
      url: blogLink,
      external,
      linkName: 'footer_blog',
      text: t('nav.blog', 'Blog'),
      show: !nonReligiousMode && isCoreLanguage,
    },
    {
      url: 'https://ancestors.familysearch.org/',
      linkName: 'footer_find_ancestors',
      text: t('nav.find-ancestors', 'Find Ancestors'),
      show: isAuthorized,
    },
    {
      url: '/dna-testing/',
      linkName: 'footer_dna',
      text: t('nav.dna', 'DNA'),
      show: isCoreLanguage && !showArabicExperience,
    },
    {
      url: 'https://www.familysearch.org/innovate/solutions-gallery',
      linkName: 'footer_solutions_gallery',
      text: t('nav.solutions-gallery', 'Solutions Gallery'),
      show: i18n.language === 'en' && !showArabicExperience,
    },
    {
      url: '/mobile-apps/',
      linkName: 'footer_mobile_apps',
      text: t('nav.mobile-apps', 'Mobile Apps'),
      show: showMobileAppsLink.isOn && isAuthorized,
    },
    {
      url: contactUsLink,
      linkName: 'footer_contact_us',
      text: t('nav.contact-us', 'Contact Us'),
    },
    {
      url: '/site-map',
      linkName: 'footer_site_map',
      text: t('nav.site-map', 'Site Map'),
      show: !showArabicExperience,
    },
  ]
}

function MinNavLinksInner({ links, viewWidth, handleExpandClick, showArabicExperience, external, ...props }) {
  const showLangPicker = useFeatureFlag('home_footer_showLangPicker').isOn
  const { t } = useTranslation()

  return (
    // Without an aria-label, this is just listed as "navigation" to screenreaders (now lists as "Footer navigation")
    <nav aria-label={t('nav.footer', 'Footer')} {...props}>
      <Row wrap alignX={viewWidth === 'mobile' ? 'center' : 'start'} alignItemsY="middle">
        <Row wrap growX alignX={viewWidth === 'mobile' ? 'center' : 'start'} alignItemsY="middle">
          {links?.map(({ show, id, url, linkName, text, ...linkProps }) =>
            show === false ? null : (
              <Link
                key={id || linkName}
                className={linkCss}
                to={buildUrl(url, external)}
                linkName={linkName}
                noLinkStyles
                isGray
                wayfinding
                external
                {...linkProps}
              >
                {text}
              </Link>
            )
          ) || (
            <Row>
              <Skeleton.Paragraph maxWidth={72} />
              <Skeleton.Paragraph maxWidth={72} />
              <Skeleton.Paragraph maxWidth={72} />
            </Row>
          )}
          {!showArabicExperience && (
            <Row alignItemsY="middle">
              |
              <Row alignY="middle" gutters="nano">
                <button
                  className={moreButton}
                  type="button"
                  onClick={handleExpandClick}
                  name="footer_more"
                  aria-label={t('nav.links.more.aria-label', 'more links')}
                >
                  <span>{t('common-ui:show-more.info.abbr', 'More')}</span>
                  <ArrowCaret background={colors.transparent.gray70} color="white" size="xs" direction="down" />
                </button>
              </Row>
            </Row>
          )}
        </Row>
        {showLangPicker && (
          <Row wrap={viewWidth === 'mobile'} alignX={viewWidth === 'mobile' ? 'center' : 'end'} alignItemsY="top">
            <LanguageLink noShrinkX />
          </Row>
        )}
      </Row>
    </nav>
  )
}

function MinNavLinks(props) {
  const { navigation } = useDynamicLinkContext()
  return <MinNavLinksInner links={navigation?.condensedFooter} {...props} />
}

function MinNavLinksOld({ nonReligiousMode, ...props }) {
  const links = useOldMinNavLinks({ nonReligiousMode, ...props })
  return <MinNavLinksInner links={links} {...props} />
}

export default function MinNavLinksFlag({ nonReligiousMode, ...props }) {
  const { isOn: dynamicLinksOn } = useFeatureFlag('home_footer_dynamicLinks')
  return dynamicLinksOn ? <MinNavLinks {...props} /> : <MinNavLinksOld nonReligiousMode={nonReligiousMode} {...props} />
}
